'use client';

import React from 'react';
import styled from '@emotion/styled';
import { usePathname } from 'next/navigation';
import { useCommonData } from '../../../../common-data/useCommonData';
import { ErrorBoundary } from '../../components/error/ErrorBoundary';
import { AlertBanners } from '../../../../components/alert-banner/AlertBanners';
import { IUrlLinkItem, Main } from '../../../../ui';
import { getFooterLinksFromEntry } from '../../../../utils/getFooterLinksFromEntry';
import { SilentErrorBoundary } from '../../components/error/SilentErrorBoundary';
import { Footer } from '../../../../components/common/footer/Footer';
import { IFooterLinkSections } from '../../../../components/common/footer/types';
import { IPageEntry } from '../../../../contentful/types/IPage';
import {
	getCurrentAirportCodeFromId,
	getCurrentAirportCodeFromSlug,
} from '../../../../components/common/navigation/utils/getCurrentAirportCode';
import { getNewsAlertForCurrentAirport } from '../../../../utils/getNewsAlertForCurrentAirport';
import { PageRoutes } from '../../../app/pages/domain/constants/PageRoutes';
import { ISocialLinksProps } from '../../../../components/common/social-links/SocialLinks';
import { ISiteSectionEntry } from '../../../../contentful/types/ISiteSection';
import { Color } from '../../../../styles/Color';
import { AppBanner } from '../../../../components/app-banner/AppBanner';

export interface IPageLayoutProps {
	children?: React.ReactNode;
	withoutFooter?: boolean;
	page?: IPageEntry;
	siteSection?: ISiteSectionEntry;
	isDarkBackgroundVariant?: boolean;
}

export const PageLayout = ({
	children,
	withoutFooter = false,
	page,
	siteSection,
	isDarkBackgroundVariant = false,
}: IPageLayoutProps) => {
	const { pathToPageIdMap, newsAlerts } = useCommonData();
	const pathname = usePathname();
	const isGatewayPage = pathname === PageRoutes.Gateway;
	const airportCode = isGatewayPage
		? 'gateway'
		: page
		? getCurrentAirportCodeFromId(page?.fields?.parent?.fields?.id)
		: getCurrentAirportCodeFromSlug(pathname);

	// Only one alert can be shown at a time
	const newsAlert = getNewsAlertForCurrentAirport(airportCode, newsAlerts);

	const footer = siteSection?.fields?.footer;
	let linkSections;

	if (footer) {
		linkSections = getFooterLinksFromEntry(footer, pathToPageIdMap);
	}

	return (
		<LayoutContentWrap isDarkBackgroundVariant={isDarkBackgroundVariant}>
			<header>
				<AppBanner />
				{newsAlert && (
					<AlertBanners
						title={newsAlert.fields.title}
						body={newsAlert.fields.body}
						link={newsAlert.fields.link}
					/>
				)}
			</header>
			<ErrorBoundary>
				<Main>{children}</Main>
			</ErrorBoundary>

			{footer && !withoutFooter && (
				<SilentErrorBoundary>
					<Footer
						appStoreLinks={linkSections?.appStoreLinks as ISocialLinksProps}
						linkSections={linkSections?.linkSections as IFooterLinkSections}
						socialLinks={linkSections?.socialLinks as ISocialLinksProps}
						finePrint={linkSections?.finePrintLinks.links as IUrlLinkItem[]}
					/>
				</SilentErrorBoundary>
			)}
		</LayoutContentWrap>
	);
};

export const LayoutContentWrap = styled.div<{
	isDarkBackgroundVariant?: boolean;
}>`
	display: block;
	margin: 0;
	position: relative;
	overflow: hidden;

	${({ isDarkBackgroundVariant }) =>
		isDarkBackgroundVariant && `background-color: ${Color.Neutral2};`}
`;
